<template>
  <PageContainer title="Alunos cadastrados">
    <div class="loading" v-if="!loaded">
      <LoadingSpinner />
    </div>

    <Box v-else class="alunos-container" space="medium">
      <form @submit.prevent="handleSubmit">
        <div class="inputs">
          <div class="inputs-row">
            <Select
              class="input-full"
              :options="schemaOptions"
              legend="Autoescola"
              placeholder="Selecione"
              :modelValue="schema"
              @update:modelValue="$event => (this.schema = $event)"
            />

            <SingleDate
              class="input-full input-processo"
              legend="Início do processo"
              :modelValue="inicioProcesso"
              @update:modelValue="$event => (this.inicioProcesso = $event)"
            />
          </div>

          <div class="inputs-row">
            <Input
              class="input-full"
              :modelValue="nomeAluno"
              @update:modelValue="$event => (this.nomeAluno = $event)"
              legend="Aluno"
              placeholder="Nome aluno"
              :disabled="loading"
            />

            <Input
              class="input-full"
              mask="###.###.###-##"
              :modelValue="cpf"
              @update:modelValue="$event => (this.cpf = $event)"
              legend="CPF"
              placeholder="CPF"
              :disabled="loading"
            />

            <Input
              class="input-full"
              :modelValue="renach"
              @update:modelValue="$event => (this.renach = $event)"
              legend="Renach"
              placeholder="Renach"
              :disabled="loading"
            />
          </div>

          <div class="buttons">
            <Button
              type="button"
              @handle:click="clearInputs"
              text="Limpar"
              :fill="false"
              color="danger"
              :disabled="loading"
            >
              <CloseCircleOutline :size="18" />
            </Button>

            <Button type="submit" text="Consultar" color="primary" :disabled="loading">
              <Magnify :size="18" />
            </Button>
          </div>
        </div>
      </form>

      <div class="loading" v-if="loading">
        <LoadingSpinner />
      </div>

      <div v-else-if="alunos.length > 0 && !loading" class="table-container">
        <Paginate
          :total-pages="paginationData.lastPage"
          :current-page="paginationData.currentPage"
          @page-changed="fetchAlunos"
        />

        <Table
          :headers="[
            'Status',
            'Aluno',
            'CPF',
            'Abertura renach',
            'LADV início',
            'LADV fim',
            'Ações',
          ]"
        >
          <tr v-for="aluno in alunos" :key="aluno.id_aluno">
            <td>
              <Badge v-if="aluno.flg_ativo" text="Ativo" color="success" />
              <Badge v-else text="Inativo" color="danger" />
            </td>
            <td>{{ aluno.nome }}</td>
            <td>{{ utils.formatarCPF(aluno.cpf) }}</td>
            <td v-if="aluno.abertura_renach">{{ utils.formatOnlyDate(aluno.abertura_renach) }}</td>
            <td v-else>-</td>
            <td v-if="aluno.ladv_inicio">{{ utils.formatOnlyDate(aluno.ladv_inicio) }}</td>
            <td v-else>-</td>
            <td v-if="aluno.ladv_fim">{{ utils.formatOnlyDate(aluno.ladv_fim) }}</td>
            <td v-else>-</td>
            <td>
              <div class="actions">
                <div>
                  <IconButton
                    @handle:click="openInfoModal(aluno.id_aluno)"
                    color="secondary"
                    title="Ver informações do aluno"
                    :disabled="loadingActions"
                  >
                    <InformationOutline :size="19" />
                  </IconButton>
                </div>
              </div>
            </td>
          </tr>
        </Table>
      </div>
    </Box>

    <InfoModal ref="infoModal" :autoescolas="schemaOptions" @updateStudent="fetchAlunos" />
  </PageContainer>
</template>

<script>
import Badge from 'core/Badge'
import Box from 'core/Box'
import Button from 'core/Button'
import IconButton from 'core/IconButton'
import Input from 'core/Input'
import LoadingSpinner from 'core/LoadingSpinner'
import PageContainer from 'core/PageContainer'
import Paginate from 'core/Paginate'
import Select from 'core/Select'
import SingleDate from 'core/SingleDate'
import Table from 'core/Table'
import CloseCircleOutline from 'icons/CloseCircleOutline'
import CloudRefreshVariantOutline from 'icons/CloudRefreshVariantOutline'
import InformationOutline from 'icons/InformationOutline'
import LockOpenOutline from 'icons/LockOpenOutline'
import LockOutline from 'icons/LockOutline'
import Magnify from 'icons/Magnify'
import TrayArrowDown from 'icons/TrayArrowDown'
import utils from '../../../services/utils'
import InfoModal from './InfoModal'

export default {
  name: 'ListarAlunos',
  components: {
    PageContainer,
    Box,
    Button,
    Input,
    SingleDate,
    Badge,
    Table,
    Paginate,
    IconButton,
    Select,
    LoadingSpinner,

    LockOutline,
    LockOpenOutline,
    InformationOutline,
    CloudRefreshVariantOutline,
    TrayArrowDown,
    CloseCircleOutline,
    Magnify,

    InfoModal,
  },
  data() {
    return {
      utils: utils,
      schema: '',
      nomeAluno: '',
      cpf: '',
      renach: '',
      inicioProcesso: '',
      paginationData: {
        currentPage: 1,
        lastPage: 0,
        total: 0,
      },
      schemaOptions: [],
      alunos: [],
      loaded: false,
      loading: false,
      loadingActions: false,
    }
  },

  async created() {
    await this.fetchCfcs()

    this.loaded = true
  },

  methods: {
    async fetchCfcs() {
      await this.$http
        .get(`${process.env.VUE_APP_URL_API_V2}/autoescola/todas`)
        .then(res => {
          this.schemaOptions = res.body.dados.map(cfc => ({
            value: cfc.tenant_schema,
            text: cfc.nome_fantasia,
          }))
        })
        .catch(error => {
          this.$toasted.show('Erro!' + error.body.mensagem, {
            position: 'top-right',
          })
        })
    },
    async fetchAlunos(page = 1) {
      this.loading = true

      const params = {
        page,
        schema: this.schema,
      }

      if (this.nomeAluno) {
        params.nome = this.nomeAluno
      }
      if (this.cpf) {
        params.cpf = this.cpf
      }
      if (this.renach) {
        params.renach = this.renach
      }
      if (this.inicioProcesso) {
        params.inicio_processo = this.inicioProcesso
      }

      this.filters = params

      await this.$http
        .get(`${process.env.VUE_APP_URL_API_V2}/alunos/consultar`, {
          params,
        })
        .then(response => {
          const data = response.body.dados

          if (!data.data.length) {
            this.$toasted.show('Sem resultados para sua pesquisa.', {
              position: 'top-right',
            })
          }

          this.alunos = data.data

          this.paginationData = {
            currentPage: data.current_page,
            lastPage: data.last_page,
            total: data.total,
          }
        })
        .catch(err => {
          this.$toasted.show(`Erro ao buscar alunos: ${err.body.mensagem || ''}`, {
            position: 'top-right',
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    async handleSubmit() {
      if (this.validateInputs()) {
        this.fetchAlunos()
      }
    },
    validateInputs() {
      if (this.cpf && this.cpf.length < 11) {
        this.$toasted.show('CPF Inválido.', {
          position: 'top-right',
        })

        return false
      }

      if (!this.schema) {
        this.$toasted.show('Selecione uma autoescola.', {
          position: 'top-right',
        })

        return false
      }

      return true
    },
    clearInputs() {
      this.schema = ''
      this.nomeAluno = ''
      this.cpf = ''
      this.renach = ''
      this.inicioProcesso = ''
    },
    openInfoModal(idAluno) {
      this.$refs.infoModal.open(idAluno, this.schema)
    },
  },
}
</script>

<style lang="scss" scoped>
.loading {
  display: flex;
  justify-content: center;
  align-items: center;
}

.alunos-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;

  form {
    display: flex;
    flex-direction: column;
    gap: 2.5rem;

    .inputs {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;

      .inputs-row {
        display: flex;
        flex-wrap: wrap;
        gap: 1.5rem;

        .input-full {
          flex: 1;
        }

        .input-processo {
          max-width: 26.625rem;
        }
      }
    }

    .buttons {
      align-self: flex-end;

      display: flex;
      gap: 0.5rem;
    }
  }

  .table-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .pagination-container {
      align-self: flex-end;
    }

    .actions {
      display: flex;
      align-items: center;

      div {
        padding: 0 0.5rem;
        display: flex;
        justify-content: center;

        &:not(:first-child) {
          border-left: 1px solid #ececec;
        }

        &:not(:last-child) {
          border-right: 1px solid #ececec;
        }
      }
    }
  }
}
</style>
